import { AfterViewInit, Directive, ErrorHandler, inject, Input, OnDestroy } from '@angular/core';
import { StrakitSelectComponent } from '@ngx-strakit/cdk/controls/select';
import { isNil, omitBy } from 'lodash';
import { isObservable, Observable, of, ReplaySubject, Subject, defer, EMPTY } from 'rxjs';
import { pairwise, share, startWith, switchMap, take, takeUntil, tap, catchError } from 'rxjs/operators';
import { IrisSkSelectFieldSearchEngine, SEARCH_ENGINE } from './search-engine.sk';
import {
  ADD_TAG,
  AUTO_OPEN,
  BIND_LABEL,
  BIND_VALUE,
  DROPDOWN_FOOTER_TEMPLATE,
  DROPDOWN_HEADER_TEMPLATE,
  EDITABLE_SEARCH_TERM,
  GROUP_BY,
  ITEMS,
  LABEL_TEMPLATE,
  MAX_ITEMS,
  MULTIPLE,
  NOT_FOUND_TEMPLATE, NOT_FOUND_TEXT,
  OPTGROUP_TEMPLATE,
  OPTION_TEMPLATE,
  PLACEHOLDER,
  SELECTABLE_GROUP,
  SELECTABLE_GROUP_AS_MODEL,
} from './properties-tokens.sk';
import { NG_SELECT_EVENT_HANDLER, NgSelectEvent, NgSelectEventHandler } from '@iris/common/modules/fields/sk-select-field/ng-select-event-handler.sk';
import { NG_SELECT_EVENTS_PROP } from '@iris/common/modules/fields/sk-select-field/decorators.sk';
import { emptyIrisPage } from '@iris/common/models/page';
import { outputToObservable } from '@angular/core/rxjs-interop';

const DEFAULT_NEED_SCROLL_TO_TOP = <TItem>(prev: TItem[], curr: TItem[]): boolean => {
  return prev?.length > curr?.length;
};

@Directive({
  selector: 'sk-select',
  exportAs: 'irisSkSelectField',
  standalone: true,
})
export class IrisSkSelectFieldDirective implements OnDestroy, AfterViewInit {
  select = inject(StrakitSelectComponent, { optional: true });
  searchEngine = inject<IrisSkSelectFieldSearchEngine<unknown, unknown>>(SEARCH_ENGINE, { optional: true });
  private readonly autoOpen = inject(AUTO_OPEN, { optional: true });
  private readonly errorHandler = inject(ErrorHandler);

  @Input() dropdownWidth: 'auto' | null;

  readonly _destroyed = new Subject<void>();

  constructor(
    // eslint-disable-next-line @angular-eslint/prefer-inject
    readonly outerSelect?: StrakitSelectComponent,
  ) {
    this.select = outerSelect ?? this.select;
    const select = this.select;
    const searchEngine = this.searchEngine;
    const headerTemplate = inject(DROPDOWN_HEADER_TEMPLATE, { optional: true });
    const footerTemplate = inject(DROPDOWN_FOOTER_TEMPLATE, { optional: true });
    const labelValueTemplate = inject(LABEL_TEMPLATE, { optional: true });
    const optionTemplate = inject(OPTION_TEMPLATE, { optional: true });
    const bindLabel = inject(BIND_LABEL, { optional: true });
    const bindValue = inject(BIND_VALUE, { optional: true });
    const groupBy = inject(GROUP_BY, { optional: true });
    const selectableGroup = inject(SELECTABLE_GROUP, { optional: true });
    const selectableGroupAsModel = inject(SELECTABLE_GROUP_AS_MODEL, { optional: true });
    const multiple = inject(MULTIPLE, { optional: true });
    const maxSelectedItems = inject(MAX_ITEMS, { optional: true });
    const placeholder = inject(PLACEHOLDER, { optional: true });
    const optgroupTemplate = inject(OPTGROUP_TEMPLATE, { optional: true });
    const items = inject(ITEMS, { optional: true });
    const addTag = inject(ADD_TAG, { optional: true });
    const autoOpen = this.autoOpen;
    const editableSearchTerm = inject(EDITABLE_SEARCH_TERM, { optional: true });
    const notFoundTemplate = inject(NOT_FOUND_TEMPLATE, { optional: true });
    const notFoundText = inject(NOT_FOUND_TEXT, { optional: true });
    const eventHandlers = inject<NgSelectEventHandler[]>(NG_SELECT_EVENT_HANDLER, { optional: true });

    const definedOptions = omitBy({
      items,
      addTag,
      bindLabel,
      bindValue,
      groupBy,
      selectableGroup,
      selectableGroupAsModel,
      multiple,
      maxSelectedItems,
      placeholder,
      editableSearchTerm,
      notFoundText,
      headerTemplateInput: headerTemplate,
      footerTemplateInput: footerTemplate,
      labelValueTemplateInput: labelValueTemplate,
      optionTemplateInput: optionTemplate,
      optgroupTemplateInput: optgroupTemplate,
      notFoundTemplateInput: notFoundTemplate,
    }, isNil);
    Object.assign(select, definedOptions);

    if (items) {
      select.refreshItems();
    }

    if (this.searchEngine) {
      this.select.typeahead = this.searchEngine.typeahead;
      this.select.getMissingItemLabelFn = this.searchEngine.getMissingItemLabelFn?.bind(this.searchEngine) as typeof this.select.getMissingItemLabelFn;

      const load$ = this.searchEngine.reloadOnOpen
        ? outputToObservable(this.select.openEvent)
        : outputToObservable(this.select.focusEvent).pipe(
          take(1),
          share({
            connector: () => new ReplaySubject(1),
            resetOnError: false,
            resetOnComplete: false,
            resetOnRefCountZero: false,
          }),
        );

      this.searchEngine.loading$?.pipe(
        tap(loading => this.select.loading = loading),
        tap(() => setTimeout(() => this.select.detectChanges())),
        takeUntil(this._destroyed),
      ).subscribe();

      (this.searchEngine.resultOnInit ? of(null) : load$).pipe(
        switchMap(() => this.searchEngine.result$.pipe(
          this.searchEngine.reloadOnOpen ? startWith(emptyIrisPage()) : tap(),
          this.searchEngine.reloadOnOpen ? takeUntil(outputToObservable(this.select.blurEvent)) : tap(),
        )),
        startWith(null),
        pairwise(),
        tap(([prevPage, currPage]) => {
          const needScrollToTop = (this.searchEngine.needScrollToTop ?? DEFAULT_NEED_SCROLL_TO_TOP)(prevPage?.elements, currPage?.elements);
          this.select.items = currPage.elements;
          this.select.refreshItems();
          if (needScrollToTop && this.select.dropdownPanel?.items?.length) {
            this.select.dropdownPanel.scrollTo(this.select.dropdownPanel.items[0]);
          }
        }),
        takeUntil(this._destroyed),
      ).subscribe();

      const searchEngineConstructor = Object.getPrototypeOf(this.searchEngine)?.constructor;
      if (searchEngineConstructor?.[NG_SELECT_EVENTS_PROP]) {
        Object.getOwnPropertyNames(searchEngineConstructor[NG_SELECT_EVENTS_PROP]).forEach((eventName: NgSelectEvent) => {
          const props: string[] = searchEngineConstructor[NG_SELECT_EVENTS_PROP]?.[eventName];
          (this.select[eventName] as Observable<unknown>).pipe(
            tap(e => props.forEach(prop => this.searchEngine[prop]?.next?.(e))),
            takeUntil(this._destroyed),
          ).subscribe();
        });
      }
    }

    if (eventHandlers?.length) {
      eventHandlers.forEach(handler => {
        (this.select[handler.eventType] as Observable<unknown>).pipe(
          switchMap(event => defer(() => {
            const res = handler.handle(this.select, event);
            return isObservable(res) ? res : of(res);
          }).pipe(
            catchError(err => {
              this.errorHandler.handleError(err); // Log the error but don't break the stream
              return EMPTY;
            }),
          )),
          takeUntil(this._destroyed),
        ).subscribe();
      });
    }

    if (autoOpen) {
      if (searchEngine) {
        searchEngine.result$.pipe(
          take(1),
          tap(() => setTimeout(() => {
            this.select.open();
            this.select.focus();
          })),
        ).subscribe();
      } else if (items) {
        setTimeout(() => {
          this.select.open();
          this.select.focus();
        });
      }
    }

    outputToObservable(select.clearEvent).pipe(
      tap(() => select.open()),
      takeUntil(this._destroyed),
    ).subscribe();
  }

  // Workaround for auto-opening
  ngAfterViewInit(): void {
    if (this.autoOpen) { setTimeout(() => this.select.open()); }
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
    this.searchEngine?.dispose?.();
  }
}
